import clsx from 'clsx'
import {useFormik} from 'formik'
import {isUndefined} from 'lodash'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, Navigate, useNavigate, useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import {useAuth} from '..'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {Button} from '../../../../_gori/partials/widgets'
import {InputPassword} from '../../../../_gori/partials/widgets/form/InputPassword'
import UserService from '../../users/core/_requests'
import AuthService from '../core/_requests'
import axios from 'axios'
import {StorageHelpers, useFeatureEnabled} from '../../../../_gori/helpers'
import {
  ACCEPT_TERM_UPIC_KEY,
  CHATBOT_WELCOME_VISIBLE_KEY,
  FEATURES,
  ROUTES,
} from '../../../../_gori/constants'
import {OrderStatus} from '../../orders/core/_const'
import Cookies from 'js-cookie'
import OptService from '../../opt/core/_requests'
import {TwoFactorAuthModal} from '../Modal/TwoFactorAuthModal'
import {VerifyManagerTokenResponse} from '../core/_models'
import {SeoHead} from '../../seo'

export function Login() {
  const intl = useIntl()
  const isRegisterEnabled = useFeatureEnabled(FEATURES.REGISTER)
  const SIGNATURE_ACCESS_KEY = process.env.REACT_APP_SIGNATURE_ACCESS_KEY
  const {newCancelToken, isCancel} = useCancelToken()
  const {stringYup, authYup} = UseYupValidate()
  const {saveAuth, handleSaveCurrentUser} = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('token')

  const passwordRef = useRef<HTMLInputElement | null>(null)
  const [managerInvite, setManagerInvite] = useState<VerifyManagerTokenResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [statusForm, setStatusForm] = useState<
    {status: 'success' | 'error'; message: string} | undefined
  >(undefined)
  const [show2FAModal, setShow2FAModal] = useState<boolean>(false)
  const [twoFactorData, setTwoFactorData] = useState<any | null>(null)

  const loginSchema = Yup.object().shape({
    username: stringYup(100, 'EMAIL'),
    password: authYup.password(),
  })
  const initialValues = {
    username: '',
    password: '',
  }

  /**
   * Complete login process after authentication
   * Used for both normal login and 2FA verification
   */
  const completeLoginProcess = async (authResponse: any) => {
    await saveAuth(authResponse)

    const config = {cancelToken: newCancelToken()}
    delete axios.defaults.headers.common[`${SIGNATURE_ACCESS_KEY}`]
    const user = await UserService.getUser(config)
    handleSaveCurrentUser(user)

    const getAcceptUPic = await OptService.getPolicyAcceptanceUPic()
    Cookies.set(ACCEPT_TERM_UPIC_KEY, getAcceptUPic.is_accepted ?? false)

    if (user.onboard_step === 'done') {
      navigate(`${ROUTES.ORDERS.BASE}?status=${OrderStatus.OPEN}`)
    } else {
      navigate(ROUTES.AUTH.ONBOARDING)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setStatusForm(undefined)
      try {
        StorageHelpers.setItemLocalStorage(CHATBOT_WELCOME_VISIBLE_KEY, true)
        setLoading(true)
        const config = {cancelToken: newCancelToken()}

        const payload = {
          ...values,
          ...(managerInvite && inviteToken ? {token: inviteToken} : {}),
        }

        const loginResponse = await AuthService.login(payload, config)

        if (loginResponse && loginResponse.verify === false) {
          setTwoFactorData({
            ...loginResponse,
            username: values.username,
          })
          setShow2FAModal(true)
          setLoading(false)
          return
        }

        // Continue with normal login flow (token valid, bypass 2FA)
        if (loginResponse) {
          await completeLoginProcess(loginResponse)
        }
      } catch (error: any) {
        if (isCancel(error)) return
        saveAuth(undefined)
        let errorMessage = error?.response?.data?.error?.message
        if (errorMessage) {
          setStatusForm({
            status: 'error',
            message: intl.formatMessage({id: errorMessage}),
          })
        }
      } finally {
        setLoading(false)
      }
    },
  })

  const handle2FAVerifySuccess = async (verifyResponse: any) => {
    try {
      if (!twoFactorData) return
      setShow2FAModal(false)
      setLoading(true)
      await completeLoginProcess(verifyResponse)
      setShow2FAModal(false)
    } catch (error: any) {
      if (isCancel(error)) return
      setStatusForm({
        status: 'error',
        message: intl.formatMessage({id: 'VERIFICATION_FAILED'}),
      })
    } finally {
      setLoading(false)
    }
  }

  const handle2FAClose = () => {
    setShow2FAModal(false)
    setTwoFactorData(null)
  }

  const verifyInviteToken = useCallback(async () => {
    if (!inviteToken) return

    try {
      const response = (await AuthService.verifyManagerToken(
        {token: inviteToken},
        {cancelToken: newCancelToken()}
      )) as VerifyManagerTokenResponse

      setManagerInvite(response)
    } catch (error: any) {
      if (isCancel(error)) return
      setManagerInvite(null)
    }
  }, [inviteToken, newCancelToken, isCancel])

  useEffect(() => {
    verifyInviteToken()
  }, [verifyInviteToken])

  if (managerInvite && inviteToken && managerInvite.is_new_user) {
    return (
      <Navigate
        to={`${ROUTES.AUTH.ACCEPT_MANAGER}?token=${encodeURIComponent(inviteToken)}`}
        replace
      />
    )
  }

  return (
    <>
      <SeoHead />
      {show2FAModal && twoFactorData && (
        <TwoFactorAuthModal
          show={show2FAModal}
          handleClose={handle2FAClose}
          onVerifySuccess={handle2FAVerifySuccess}
          data={twoFactorData}
        />
      )}
      {managerInvite && (
        <div
          className='d-flex justify-content-center text-center invite-message fw-bold fs-4'
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage(
              {id: 'MANAGER_INVITATION_LOGIN_MESSAGE'},
              {organization_name: `<span class="fw-bolder">${managerInvite.company_name}</span>`}
            ),
          }}
        />
      )}
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
            <div className='text-center mb-10'>
              <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'SIGN_IN'})}</h1>
              {isRegisterEnabled && (
                <div className='text-gray-400 fw-bold fs-4'>
                  {intl.formatMessage({id: 'NEW_HERE'})}?{' '}
                  <Link
                    to={ROUTES.AUTH.REGISTRATION}
                    className='text-primary-deep text-hover-primary fw-bolder'
                  >
                    {intl.formatMessage({id: 'CREATE_AN_ACCOUNT'})}
                  </Link>
                </div>
              )}
            </div>
            {!isUndefined(statusForm) && (
              <div
                className={clsx('mb-lg-15 alert', {
                  'bg-light-primary': statusForm.status === 'success',
                  'alert-danger': statusForm.status === 'error',
                })}
              >
                <div className='alert-text font-weight-bold'>{statusForm.message}</div>
              </div>
            )}
            <div className='fv-row mb-10 fv-plugins-icon-container'>
              <label className={`form-label form-label fw-bolder text-dark fs-6 required`}>
                {intl.formatMessage({id: 'EMAIL'})}
              </label>
              <div>
                <div className='input-group'>
                  <input
                    autoFocus
                    type='text'
                    tabIndex={1}
                    autoComplete='off'
                    className={clsx(
                      `form-control form-control-lg`,
                      {
                        'is-invalid': formik.touched.username && formik.errors.username,
                      },
                      {
                        'is-valid':
                          formik.touched.username &&
                          !formik.errors.username &&
                          formik.values.username?.trim(),
                      }
                    )}
                    {...formik.getFieldProps('username')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (
                          formik.values.username?.trim() &&
                          !formik.values.password?.trim() &&
                          passwordRef.current
                        ) {
                          passwordRef.current.focus()
                        } else if (
                          formik.values.username?.trim() &&
                          formik.values.password?.trim()
                        ) {
                          formik.handleSubmit()
                        }
                      }
                    }}
                  />
                </div>
                {formik.touched.username && formik.errors.username && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block text-danger'>
                      <span role='alert'>{formik.errors.username}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='fv-row mb-10 fv-plugins-icon-container'>
              <div className='d-flex flex-stack mb-2'>
                <label className='form-label fw-bolder text-dark fs-6 mb-0 required'>
                  {intl.formatMessage({id: 'PASSWORD'})}
                </label>
                <Link
                  to={'/auth/forgot-password'}
                  tabIndex={1000}
                  className='text-primary-deep text-hover-primary fs-6 fw-bolder'
                >
                  {intl.formatMessage({id: 'FORGOT_PASSWORD'})} ?
                </Link>
              </div>
              <InputPassword
                ref={passwordRef}
                tabIndex={2}
                size='lg'
                openHighlight={false}
                formik={formik}
                name={'password'}
                required
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    formik.handleSubmit()
                  }
                }}
              />
            </div>
            <div className='text-center'>
              <Button
                className='btn btn-lg btn-advanced-primary w-100 mb-5'
                label={intl.formatMessage({id: 'SUBMIT'})}
                loadingText={intl.formatMessage({id: 'LOGGING_IN'})}
                loading={loading}
                disabled={loading}
                event={formik.submitForm}
              />
            </div>
          </div>
        </div>
      </CSSTransition>
    </>
  )
}
